import React from "react";
import {Col, List, Popconfirm, Row, Space, Tag, Tooltip} from "antd";
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import {DeleteOutlined, EditOutlined, EyeOutlined} from "@ant-design/icons";

// Types
import {AllianceStockType} from "../types/AllianceStockTypes";
import {CategoryType} from "../types/CategoryTypes";
import {AllianceType} from "../types/AllianceTypes";
import {OrderOmcType} from "../types/OrderTypes";
import {PaymentOmcType} from "../types/PaymentTypes";
import {ShipmentOmcType} from "../types/ShipmentTypes";

// Utils
import {currencyFormat, invertColor} from "./generalUtils";
import {FieldTranslation} from "./functions";

// Constants
import {DATETIME_FORMAT} from "./constants"


const getColorInverted = (color: string, name: string) => {
  let colorInverted = invertColor(color);

  return <Tag style={{ backgroundColor: color, color: colorInverted}}>
    {name}
  </Tag>
}

const tableColumns = (
  section: string, 
  callback?: (param?: any) => void, 
  removeCallback?: (param?: any) => void
  ) => {
  dayjs.extend(utc);
  switch (section){
    case 'catalog':
      return [
        { title: '', dataIndex: 'firstColumn', key: 'firstColumn', width: '100px'},
        { title: '', dataIndex: 'secondColumn', key: 'secondColumn', width: '2000px' },
      ];
    case 'variants':
      return [
        { title: <FieldTranslation field={'code'} />, dataIndex: 'code', key: 'code', width: '100px' },
        { title: <FieldTranslation field={'name'} />, dataIndex: 'name', key: 'name', width: '200px' },
        { title: <FieldTranslation field={'stock'} />, dataIndex: 'stock', key: 'stock', width: '100px' },
        { title: <FieldTranslation field={'allianceStocks'} />, dataIndex: 'allianceStocks', key: 'allianceStocks', width: '300px',
          render: (stocks: AllianceStockType[]) =>
            <List itemLayout={"horizontal"}
                  size={'small'}
                  dataSource={stocks}
                  renderItem={(stock) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Col>
                            <Tag color={stock.color}>
                              {stock.name}
                            </Tag>
                            <Tooltip title={<FieldTranslation field={'edit'} />}>
                              <EditOutlined className={'inline-block float-right mx-2 text-blue-dkt-400'}
                                            onClick={() => callback && callback(stock)} />
                            </Tooltip>
                          </Col>
                        }
                        description={
                          <Row >
                            <Col md={24} xs={24}>
                              <Space>
                                <Col>
                                  <span className={'font-semibold'}><FieldTranslation field={'discountPrice'} />:</span> <FieldTranslation field={'currencySymbol'} /> {currencyFormat(stock.price.toString())}
                                </Col>
                                <Col>
                                  <span className={'font-semibold'}><FieldTranslation field={'baseStock'} />:</span> {stock.baseStock}
                                </Col>
                              </Space> 
                            </Col>
                            {stock.promotionStartDate && <Col md={24} xs={24}>
                              <span className={'font-semibold'}><FieldTranslation field={'promotionStartDate'} />:</span> {dayjs.utc(stock.promotionStartDate).format(DATETIME_FORMAT)}
                            </Col>}
                            {stock.promotionEndDate && <Col md={24} xs={24}>
                              <span className={'font-semibold'}><FieldTranslation field={'promotionEndDate'} />:</span> {dayjs.utc(stock.promotionEndDate).format(DATETIME_FORMAT)}
                            </Col>}
                          </Row>
                        }
                      />
                    </List.Item>
                  )}
            />
        },
      ];
    case 'category':
      return [
        { title: <FieldTranslation field={'model'} />, dataIndex: 'model', key: 'model', width: '100px' },
        { title: <FieldTranslation field={'category'} />, dataIndex: 'primaryCategory', key: 'primaryCategory', width: '500px' },
        { title: <FieldTranslation field={'actions'} />, dataIndex: '', key: 'action', width: '100px',
          render: (category: CategoryType) =>
            <Space>
              <Tooltip title={<FieldTranslation field={'edit'} />}>
                <EditOutlined className={'inline-block mx-2 text-blue-dkt-400'}
                              onClick={() => callback && callback(category)}/>
              </Tooltip>
              <Popconfirm title={<FieldTranslation field={'removeCategoryConfirm'} />}
                          okText={<FieldTranslation field={'confirm'} />}
                          cancelText={<FieldTranslation field={'cancel'} />}
                          onConfirm={() => removeCallback && removeCallback(category.categoryId)}
              >
                <p className={'text-red-dkt-400 cursor-pointer mt-1'}>
                  <Tooltip title={<FieldTranslation field={'remove'} />}>
                    <DeleteOutlined />
                  </Tooltip>
                </p>
              </Popconfirm>
            </Space>
        },
      ];
    case 'orders':
      return [
        { title: <FieldTranslation field={'order'} />, dataIndex: 'externalOrderId', key: 'externalOrderId', width: '80px' },
        { title: <FieldTranslation field={'items'} />, dataIndex: 'items', key: 'items', width: '100px' },
        { title: <FieldTranslation field={'payments'} />, dataIndex: 'paymentIds', key: 'paymentIds', width: '100px' },
        { title: <FieldTranslation field={'totalAmount'} />, dataIndex: 'totalAmount', key: 'totalAmount', width: '150px' },
        { title: <FieldTranslation field={'paidAmount'} />, dataIndex: 'paidAmount', key: 'paidAmount', width: '150px' },
        { title: <FieldTranslation field={'creationDate'} />, dataIndex: 'creationDate', key: 'creationDate', width: '150px',
          render: ((creationDate: string) => <>{dayjs.utc(creationDate).format(DATETIME_FORMAT)}</>)
        },
        { title: <FieldTranslation field={'alliance'} />, dataIndex: 'alliance', key: 'alliance', width: '100px',
          render: (alliance: AllianceType) => getColorInverted(alliance.color!, alliance.name)
        }, 
        { title: <FieldTranslation field={'actions'} />, dataIndex: '', key: 'action', width: '50px',
          render: (order: OrderOmcType) =>
            <Space>
              <Tooltip title={<FieldTranslation field={'see'} />}>
                <EyeOutlined className={'inline-block mx-2 text-blue-dkt-400 align-middle'}
                             onClick={() => callback && callback(order.orderId)} />
              </Tooltip>
            </Space>
        },    
      ];
    case 'payments':
        return [
          { title: <FieldTranslation field={'payment'} />, dataIndex: 'externalPaymentId', key: 'externalPaymentId', width: '80px' },
          { title: <FieldTranslation field={'order'} />, dataIndex: 'externalOrderId', key: 'externalOrderId', width: '80px' },
          { title: <FieldTranslation field={'creationDate'} />, dataIndex: 'creationDate', key: 'creationDate', width: '100px',
            render: ((creationDate: string) => <>{dayjs.utc(creationDate).format(DATETIME_FORMAT)}</>)
          },
          { title: <FieldTranslation field={'alliance'} />, dataIndex: 'alliance', key: 'alliance', width: '100px',
            render: (alliance: AllianceType) => getColorInverted(alliance.color!, alliance.name)
          }, 
          { title: <FieldTranslation field={'actions'} />, dataIndex: '', key: 'action', width: '50px',
            render: (payment: PaymentOmcType) =>
              <Space>
                <Tooltip title={<FieldTranslation field={'see'} />}>
                  <EyeOutlined className={'inline-block mx-2 text-blue-dkt-400 align-middle'}
                               onClick={() => callback && callback(payment.paymentId)}/>
                </Tooltip>
              </Space>
          },    
        ];
    case 'shipments':
        return [
          { title: <FieldTranslation field={'shipment'} />, dataIndex: 'externalShipmentId', key: 'externalShipmentId', width: '80px' },
          { title: <FieldTranslation field={'order'} />, dataIndex: 'externalOrderId', key: 'externalOrderId', width: '80px' },
          { title: <FieldTranslation field={'creationDate'} />, dataIndex: 'creationDate', key: 'creationDate', width: '100px',
            render: ((creationDate: string) => <>{dayjs.utc(creationDate).format(DATETIME_FORMAT)}</>)
          },
          { title: <FieldTranslation field={'alliance'} />, dataIndex: 'alliance', key: 'alliance', width: '100px',
            render: (alliance: AllianceType) => getColorInverted(alliance.color!, alliance.name)
          }, 
          { title: <FieldTranslation field={'actions'} />, dataIndex: '', key: 'action', width: '50px',
            render: (shipment: ShipmentOmcType) =>
              <Space>
                <Tooltip title={<FieldTranslation field={'see'} />}>
                  <EyeOutlined className={'inline-block mx-2 text-blue-dkt-400 align-middle'}
                              onClick={() => callback && callback(shipment.shipmentId)}/>
                </Tooltip>
              </Space>
          },    
        ];
    default:
      return [];
  }
};

export default tableColumns;